import { useEffect, useState } from "react";
import { Package, Truck, CheckCircle2, AlertCircle, Search, PackageCheck, Inbox, FileCheck2, Printer, Upload, Wallet, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatRupiah, formatDate, koperasiInfo } from "@/lib/dummyData";
import { OrderAPI, MerchantAPI, PaymentAPI, UploadAPI } from "@/lib/api";
import { payOrderWithMidtrans } from "@/lib/midtrans";
import { toast } from "sonner";

// Peta status backend -> label Indonesia + warna badge.
const STATUS = {
  PENDING:   { label: "Menunggu Bayar", color: "bg-stone-soft/40 text-ink/75" },
  PAID:      { label: "Dibayar",        color: "bg-forest/10 text-forest" },
  PACKED:    { label: "Dikemas",        color: "bg-amber-warm/20 text-amber-700" },
  SHIPPED:   { label: "Dikirim",        color: "bg-amber-warm/20 text-amber-700" },
  COMPLETED: { label: "Selesai",        color: "bg-sage/20 text-forest" },
  CANCELLED: { label: "Dibatalkan",     color: "bg-terracotta/15 text-terracotta" },
  COMPLAINT: { label: "Dikomplain",     color: "bg-terracotta/15 text-terracotta" },
};
const label = (s) => STATUS[s]?.label || s;
const color = (s) => STATUS[s]?.color || "bg-stone-soft/40 text-ink/75";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function MemberOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("Semua");

  // Konfirmasi pembayaran milik user (untuk tahu pesanan PENDING mana yang sudah kirim bukti).
  const [confirmations, setConfirmations] = useState([]);
  // Dialog upload bukti transfer untuk pesanan PENDING.
  const [proofTarget, setProofTarget] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofBank, setProofBank] = useState("BCA");
  const [proofSending, setProofSending] = useState(false);

  // Muat pesanan milik anggota yang login.
  const load = async () => {
    setLoading(true);
    try {
      setOrders(await OrderAPI.mine());
      setConfirmations(await PaymentAPI.mine().catch(() => []));
    } catch {
      toast.error("Gagal memuat pesanan");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Status konfirmasi terakhir untuk sebuah pesanan (PENDING = menunggu verifikasi penjual).
  const confirmationFor = (orderId) =>
    confirmations.find((c) => c.order_id === orderId && c.purpose === "ORDER");

  // Kirim bukti transfer pesanan → diverifikasi PENJUAL produk tersebut.
  const submitOrderProof = async () => {
    if (!proofFile) return toast.error("Pilih file bukti transfer dulu");
    setProofSending(true);
    try {
      const dataUrl = await fileToDataUrl(proofFile);
      const url = await UploadAPI.image(proofFile.name, dataUrl);
      await PaymentAPI.submitProof({
        order_id: proofTarget.id,
        purpose: "ORDER",
        amount: Number(proofTarget.total),
        bank_name: proofBank,
        sender_name: "",
        proof_url: url,
      });
      toast.success("Bukti terkirim — menunggu verifikasi penjual.");
      setProofTarget(null);
      setProofFile(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal mengunggah bukti");
    } finally {
      setProofSending(false);
    }
  };

  // Bayar (ulang) pesanan PENDING lewat Midtrans Snap.
  const [paying, setPaying] = useState(null);
  const payOnline = async (order) => {
    setPaying(order.id);
    try {
      const { order: latest } = await payOrderWithMidtrans(order);
      if (latest.status === "PAID") toast.success(`Pembayaran pesanan ${order.order_number} berhasil`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Gagal membuka pembayaran");
    } finally {
      setPaying(null);
    }
  };

  // Ganti metode pembayaran: transaksi Midtrans lama (VA/QR yang sudah terbit) dimatikan,
  // lalu Snap dibuka lagi dengan transaksi baru agar pembeli bisa memilih metode lain.
  const changeMethod = async (order) => {
    if (!window.confirm(
      `Ganti metode pembayaran pesanan ${order.order_number}?

Nomor VA / kode QR sebelumnya akan dinonaktifkan. Jangan bayar ke nomor lama.`
    )) return;
    setPaying(order.id);
    try {
      const res = await OrderAPI.midtransRenew(order.id);
      if (res.paid) {
        toast.success(`Pesanan ${order.order_number} ternyata sudah dibayar`);
      } else if (res.processing) {
        toast.info("Pembayaran sebelumnya sedang diproses Midtrans — tunggu beberapa saat");
      } else {
        const { order: latest } = await payOrderWithMidtrans(order, res.payment);
        if (latest.status === "PAID") toast.success(`Pembayaran pesanan ${order.order_number} berhasil`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Gagal mengganti metode pembayaran");
    } finally {
      setPaying(null);
    }
  };

  // Konfirmasi barang diterima -> backend melepas dana escrow ke penjual + bagi hasil.
  const confirmReceipt = async (order) => {
    try {
      const res = await OrderAPI.confirmDelivery(order.id);
      toast.success(`Pesanan ${order.order_number} selesai. Dana diteruskan ke penjual.`);
      if (res.revenue_split) {
        toast.info(`Bagi hasil: SHU pusat ${formatRupiah(res.revenue_split.shu_pusat)}, gerai ${formatRupiah(res.revenue_split.unit_gerai_rtrw)}`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal mengonfirmasi penerimaan");
    }
  };

  const list = orders.filter((o) =>
    (filter === "Semua" || o.status === filter) &&
    (o.order_number || "").toLowerCase().includes(q.toLowerCase())
  );

  const count = (s) => orders.filter((o) => o.status === s).length;

  return (
    <div className="space-y-7">
      <div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Pesanan Saya</h1>
        <p className="text-ink/75 mt-2">Pantau status pesanan dan konfirmasi penerimaan barang.</p>
      </div>

      {/* Ringkasan jumlah per status */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { l: "Total Pesanan", v: orders.length, c: "bg-forest/10 text-forest" },
          { l: "Dikirim", v: count("SHIPPED"), c: "bg-amber-warm/20 text-amber-700" },
          { l: "Selesai", v: count("COMPLETED"), c: "bg-sage/20 text-forest" },
          { l: "Menunggu Bayar", v: count("PENDING"), c: "bg-stone-soft/40 text-ink/75" },
        ].map((s) => (
          <Card key={s.l} className="rounded-2xl border-stone-soft p-5">
            <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold">{s.l}</div>
            <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full ${s.c}`}>
              <span className="font-display font-bold text-lg">{s.v}</span><span className="text-xs">pesanan</span>
            </div>
          </Card>
        ))}
      </div>

      {/* Pengiriman masuk dari Daerah/Cabang via supplier (khusus pemilik gerai) */}
      <IncomingShipments />

      <Card className="rounded-3xl border-stone-soft p-6">
        <div className="flex flex-col lg:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/55" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nomor pesanan..." className="pl-9 bg-cream border-stone-soft rounded-lg" data-testid="orders-search" />
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {["Semua", "PENDING", "PAID", "PACKED", "SHIPPED", "COMPLETED"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap ${filter === s ? "bg-forest text-white" : "bg-cream text-ink/75 hover:bg-stone-soft/30"}`}
                data-testid={`orders-filter-${s.toLowerCase()}`}
              >
                {s === "Semua" ? "Semua" : label(s)}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-stone-soft hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-wider">Pesanan</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Tanggal</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Resi</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Pembayaran</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Total</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center text-ink/55 py-10">Memuat…</TableCell></TableRow>
              ) : list.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-ink/55 py-10">Belum ada pesanan.</TableCell></TableRow>
              ) : list.map((o) => (
                <TableRow key={o.id} className="border-stone-soft hover:bg-cream/50" data-testid={`order-row-${o.id}`}>
                  <TableCell className="font-mono text-xs font-semibold text-ink">{o.order_number}</TableCell>
                  <TableCell className="text-sm text-ink/75">{formatDate(o.created_at)}</TableCell>
                  <TableCell><Badge className={`${color(o.status)} border-0 rounded-full text-[10px] uppercase tracking-wider`}>{label(o.status)}</Badge></TableCell>
                  <TableCell className="text-xs font-mono text-ink/75">{o.tracking_number || "—"}</TableCell>
                  <TableCell className="text-sm text-ink/75">
                    {o.payment_method}
                    {o.midtrans_payment_type && <div className="text-[11px] text-ink/50">{o.midtrans_payment_type.replace(/_/g, " ")}</div>}
                  </TableCell>
                  <TableCell className="text-right font-display font-semibold text-ink">{formatRupiah(o.total)}</TableCell>
                  <TableCell className="text-right">
                    {/* Tombol konfirmasi terima hanya muncul saat barang sudah dikirim */}
                    {o.status === "SHIPPED" ? (
                      <Button size="sm" className="rounded-full bg-forest hover:bg-forest-dark text-white h-8" onClick={() => confirmReceipt(o)} data-testid={`order-confirm-${o.id}`}>
                        <PackageCheck size={14} /> Terima Barang
                      </Button>
                    ) : o.status === "PENDING" && o.payment_method === "MIDTRANS" ? (
                      <div className="flex flex-col items-end gap-1.5">
                        <Button size="sm" disabled={paying === o.id} className="rounded-full bg-amber-warm text-ink hover:bg-amber-warm/90 h-8" onClick={() => payOnline(o)} data-testid={`order-pay-${o.id}`}>
                          <Wallet size={14} /> {paying === o.id ? "Membuka…" : "Bayar"}
                        </Button>
                        {/* Metode sudah dipilih (VA/QR terbit) → Snap terkunci; tawarkan ganti metode. */}
                        {o.midtrans_status === "pending" && (
                          <button disabled={paying === o.id} onClick={() => changeMethod(o)} className="text-[11px] text-forest hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid={`order-change-method-${o.id}`}>
                            <RefreshCw size={11} /> Ganti metode pembayaran
                          </button>
                        )}
                      </div>
                    ) : o.status === "PENDING" ? (
                      confirmationFor(o.id)?.status === "PENDING" ? (
                        <span className="text-xs text-amber-700">Menunggu verifikasi penjual</span>
                      ) : confirmationFor(o.id)?.status === "REJECTED" ? (
                        <Button size="sm" variant="outline" className="rounded-full h-8 border-terracotta/40 text-terracotta" onClick={() => setProofTarget(o)} data-testid={`order-proof-${o.id}`}>
                          <Upload size={13} /> Bukti ditolak — kirim ulang
                        </Button>
                      ) : (
                        <Button size="sm" className="rounded-full bg-amber-warm text-ink hover:bg-amber-warm/90 h-8" onClick={() => setProofTarget(o)} data-testid={`order-proof-${o.id}`}>
                          <Upload size={13} /> Upload Bukti Transfer
                        </Button>
                      )
                    ) : (
                      <span className="text-xs text-ink/45">{o.status === "COMPLETED" ? "Selesai" : o.status === "PAID" ? "Menunggu diproses penjual" : o.status === "PACKED" ? "Sedang dikemas" : "—"}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Dialog upload bukti transfer (pembayaran manual) */}
      <Dialog open={Boolean(proofTarget)} onOpenChange={(v) => { if (!v) { setProofTarget(null); setProofFile(null); } }}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Upload Bukti Transfer</DialogTitle>
            <DialogDescription>
              Pesanan <b>{proofTarget?.order_number}</b> — transfer <b>{proofTarget ? formatRupiah(proofTarget.total) : ""}</b> ke
              rekening koperasi ({koperasiInfo.bankName} {koperasiInfo.bankAccount} a.n. {koperasiInfo.bankHolder}), lalu unggah buktinya.
              Bukti akan diverifikasi oleh <b>penjual</b> produk tersebut.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <div className="space-y-1.5">
              <span className="text-xs text-ink/55 uppercase tracking-wider">Bank Pengirim</span>
              <Input value={proofBank} onChange={(e) => setProofBank(e.target.value)} className="rounded-lg" data-testid="order-proof-bank" />
            </div>
            <label htmlFor="order-proof-file" className="block border-2 border-dashed border-stone-soft rounded-2xl p-5 text-center cursor-pointer hover:bg-cream transition">
              <Upload size={20} className="mx-auto text-forest" />
              <div className="font-semibold text-sm text-ink mt-2">{proofFile ? proofFile.name : "Klik untuk pilih bukti transfer"}</div>
              <div className="text-xs text-ink/55 mt-1">JPG/PNG/WEBP, maks. 700 KB</div>
              <input id="order-proof-file" type="file" accept="image/*" className="hidden" onChange={(e) => setProofFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setProofTarget(null)}>Batal</Button>
            <Button disabled={proofSending} className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={submitOrderProof} data-testid="order-proof-submit">
              {proofSending ? "Mengirim…" : "Kirim Bukti"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Peta status Surat Pesanan (procurement).
const PO_STATUS = {
  ISSUED:   { label: "Menunggu Diterima", color: "bg-amber-warm/20 text-amber-700" },
  RECEIVED: { label: "Sudah Diterima",    color: "bg-sage/20 text-forest" },
  PAID:     { label: "Sudah Dibayar",     color: "bg-forest/10 text-forest" },
  CANCELLED:{ label: "Dibatalkan",        color: "bg-terracotta/15 text-terracotta" },
};

// Pengiriman barang masuk untuk gerai anggota + penerbitan Surat Bukti Penerimaan Barang.
function IncomingShipments() {
  const [items, setItems] = useState([]);
  const [hasShop, setHasShop] = useState(true);
  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState(null); // PO detail utk surat bukti
  const [payTarget, setPayTarget] = useState(null); // PO yang akan dibayar ke koperasi

  const load = async () => {
    setLoading(true);
    try {
      setItems(await MerchantAPI.incomingOrders());
      setHasShop(true);
    } catch (err) {
      if (err?.response?.status === 403) setHasShop(false);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const receive = async (po) => {
    try {
      await MerchantAPI.receiveIncoming(po.id);
      toast.success(`Barang PO ${po.po_number} diterima — Surat Bukti Penerimaan diterbitkan`);
      const detail = await MerchantAPI.incomingOrder(po.id);
      setReceipt(detail);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menerima barang");
    }
  };

  const openReceipt = async (po) => {
    try { setReceipt(await MerchantAPI.incomingOrder(po.id)); }
    catch { toast.error("Gagal memuat surat"); }
  };

  // Sembunyikan seluruh bagian bila user bukan pemilik gerai.
  if (!hasShop) return null;

  return (
    <Card className="rounded-3xl border-stone-soft p-6">
      <div className="flex items-center gap-2 mb-1">
        <Inbox size={18} className="text-forest" />
        <h3 className="font-display font-semibold text-ink text-lg">Pengiriman Masuk</h3>
      </div>
      <p className="text-xs text-ink/55 mb-4">Kiriman barang dari Daerah/Cabang melalui supplier. Terima barang untuk menerbitkan Surat Bukti Penerimaan Barang.</p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-soft hover:bg-transparent">
              <TableHead className="text-[10px] uppercase tracking-wider">No. Surat Pesanan</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Penerbit</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Supplier</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Tanggal</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-center">Item</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Pembayaran</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Nilai</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center text-ink/55 py-8">Memuat…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center text-ink/55 py-8">Belum ada pengiriman masuk.</TableCell></TableRow>
            ) : items.map((po) => {
              const st = PO_STATUS[po.status] || { label: po.status, color: "bg-stone-soft/40 text-ink/70" };
              return (
                <TableRow key={po.id} className="border-stone-soft hover:bg-cream/50" data-testid={`incoming-po-${po.id}`}>
                  <TableCell className="font-mono text-xs font-semibold text-ink">{po.po_number}</TableCell>
                  <TableCell className="text-sm text-ink/75">{po.issuer_org_name || "—"}</TableCell>
                  <TableCell className="text-sm text-ink/75">{po.supplier_name}</TableCell>
                  <TableCell className="text-sm text-ink/55">{formatDate(po.created_at)}</TableCell>
                  <TableCell className="text-center text-sm">{po.item_count}</TableCell>
                  <TableCell><Badge className={`${st.color} border-0 rounded-full text-[10px] uppercase tracking-wider`}>{st.label}</Badge></TableCell>
                  <TableCell>
                    {po.settlement_status === "APPROVED" ? (
                      <Badge className="bg-sage/20 text-forest border-0 rounded-full text-[10px] uppercase tracking-wider">Lunas</Badge>
                    ) : po.settlement_status === "PENDING" ? (
                      <Badge className="bg-amber-warm/20 text-amber-700 border-0 rounded-full text-[10px] uppercase tracking-wider">Verifikasi</Badge>
                    ) : po.status === "ISSUED" ? (
                      <span className="text-xs text-ink/45">—</span>
                    ) : (
                      <Badge className="bg-terracotta/15 text-terracotta border-0 rounded-full text-[10px] uppercase tracking-wider">Belum Bayar</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-display font-semibold text-ink">{formatRupiah(Number(po.total_amount))}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {po.status === "ISSUED" ? (
                        <Button size="sm" className="rounded-full bg-forest hover:bg-forest-dark text-white h-8" onClick={() => receive(po)} data-testid={`incoming-receive-${po.id}`}>
                          <PackageCheck size={14} /> Terima Barang
                        </Button>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" className="rounded-full h-8 border-stone-soft" onClick={() => openReceipt(po)} data-testid={`incoming-receipt-${po.id}`}>
                            <FileCheck2 size={14} /> Surat Bukti
                          </Button>
                          {!po.settlement_status && (
                            <Button size="sm" className="rounded-full bg-amber-warm text-ink hover:bg-amber-warm/90 h-8" onClick={() => setPayTarget(po)} data-testid={`incoming-pay-${po.id}`}>
                              <Wallet size={14} /> Bayar ke Koperasi
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <GoodsReceiptDialog po={receipt} onClose={() => setReceipt(null)} />
      <SettlementDialog po={payTarget} onClose={() => setPayTarget(null)} onPaid={load} />
    </Card>
  );
}

// Pembayaran barang gerai ke koperasi — WAJIB unggah bukti transfer.
function SettlementDialog({ po, onClose, onPaid }) {
  const [file, setFile] = useState(null);
  const [bank, setBank] = useState("");
  const [sender, setSender] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (po) { setFile(null); setBank(""); setSender(""); } }, [po]);

  const submit = async () => {
    if (!file) return toast.error("Bukti pembayaran wajib diunggah");
    setSaving(true);
    try {
      const proof = await fileToDataUrl(file);
      await MerchantAPI.settleIncoming(po.id, { proof, bank_name: bank || undefined, sender_name: sender || undefined });
      toast.success("Bukti pembayaran terkirim — menunggu verifikasi tim keuangan");
      onClose(); onPaid?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal mengirim pembayaran");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!po} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><Wallet size={18} /> Bayar ke Koperasi</DialogTitle>
          <DialogDescription>
            {po && <>Pelunasan barang PO <b>{po.po_number}</b> senilai <b>{formatRupiah(Number(po.total_amount))}</b>. Transfer ke rekening koperasi lalu unggah buktinya.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <span className="text-xs uppercase tracking-wider text-ink/55 font-semibold">Bukti Pembayaran (wajib)</span>
            <label className="flex items-center gap-2 px-3 h-10 rounded-lg border border-dashed border-stone-soft cursor-pointer text-sm text-ink/70 hover:bg-cream/50">
              <Upload size={15} /> {file ? file.name : "Pilih berkas bukti transfer…"}
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="settle-proof" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <span className="text-xs uppercase tracking-wider text-ink/55 font-semibold">Bank Pengirim</span>
              <Input value={bank} onChange={(e) => setBank(e.target.value)} placeholder="cth. BRI" className="rounded-lg" />
            </div>
            <div className="space-y-1.5">
              <span className="text-xs uppercase tracking-wider text-ink/55 font-semibold">Nama Pengirim</span>
              <Input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="Nama di rekening" className="rounded-lg" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-forest hover:bg-forest-dark text-white" data-testid="settle-submit">
            {saving ? "Mengirim…" : "Kirim Bukti Pembayaran"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Surat Bukti Penerimaan Barang — dapat dicetak.
function GoodsReceiptDialog({ po, onClose }) {
  const print = () => {
    const html = document.getElementById("goods-receipt-print")?.innerHTML;
    if (!html) return;
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) return toast.error("Popup diblokir browser");
    w.document.write(`<html><head><title>Surat Bukti Penerimaan Barang ${po.po_number}</title>
      <style>body{font-family:Georgia,serif;padding:40px;color:#1a1a1a} table{width:100%;border-collapse:collapse;margin-top:12px} th,td{border:1px solid #ccc;padding:8px;text-align:left;font-size:13px} th{background:#f3f1ea} h1{font-size:20px;margin-bottom:2px} .muted{color:#666;font-size:13px}</style>
      </head><body>${html}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  return (
    <Dialog open={!!po} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><FileCheck2 size={18} /> Surat Bukti Penerimaan Barang</DialogTitle>
          <DialogDescription>Dokumen resmi penerimaan barang oleh gerai anggota.</DialogDescription>
        </DialogHeader>
        {po && (
          <div id="goods-receipt-print" className="text-sm text-ink space-y-3">
            <div>
              <h1 className="font-display font-bold text-lg text-ink">SURAT BUKTI PENERIMAAN BARANG</h1>
              <div className="muted text-ink/60">No. Surat Pesanan: <b>{po.po_number}</b></div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-ink/80">
              <div>Diterbitkan oleh: <b>{po.issuer_org_name || "—"}</b></div>
              <div>Unit Penerima: <b>{po.destination_shop_name || po.destination_org_name || "—"}</b></div>
              <div>Supplier: <b>{po.supplier_name}</b></div>
              <div>Kontak Supplier: <b>{po.supplier_contact || "—"}</b></div>
              <div>Diterima oleh: <b>{po.receipt?.receiver_name || "—"}</b></div>
              <div>Tanggal Terima: <b>{po.receipt?.received_at ? formatDate(po.receipt.received_at) : "—"}</b></div>
            </div>
            <table>
              <thead>
                <tr><th>Nama Barang</th><th>Jumlah</th><th>Satuan</th><th>Harga Satuan</th><th>Subtotal</th></tr>
              </thead>
              <tbody>
                {(po.items || []).map((it, i) => (
                  <tr key={i}>
                    <td>{it.item_name}</td>
                    <td>{Number(it.quantity)}</td>
                    <td>{it.unit || "—"}</td>
                    <td>{formatRupiah(Number(it.unit_price))}</td>
                    <td>{formatRupiah(Number(it.line_total))}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={4} style={{ textAlign: "right", fontWeight: "bold" }}>Total</td>
                  <td style={{ fontWeight: "bold" }}>{formatRupiah(Number(po.total_amount))}</td>
                </tr>
              </tbody>
            </table>
            {po.receipt?.note && <div className="text-ink/70">Catatan: {po.receipt.note}</div>}
            <div className="muted text-ink/50 pt-2">Dokumen ini menyatakan bahwa barang telah diterima dalam kondisi baik dan sesuai surat pesanan.</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Tutup</Button>
          <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={print} data-testid="print-receipt">
            <Printer size={14} /> Cetak Surat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
